/* Ardor print template (PDF) for the A-frame sign designer. Ardor Printing, Oct 12 2026.
Adds a "Template" button to the tool rail and runs the "Download the template for this size" line in the Artwork panel
([data-abb-template]). The PDF is made in the browser for the size and sides picked in the builder: every page is the exact
print size (the same size as the print file the builder makes), one page per printed side. All guides (cut edge, dashed safe
line, zones, labels, notes and the Ardor logo) sit on one PDF layer (optional content group) named
"Ardor guides - hide before saving", so customers can hide it. Plain PDF 1.6 with standard fonts: opens in Illustrator,
Photoshop, InDesign, Canva and Acrobat. Also tells the builder whether an uploaded PDF still has that layer turned on.
window.ArdorAFrameTemplate = { spec(), build(spec?) -> Promise<{ blob, name, spec }>, download(source), guides(file) -> Promise<'on'|'off'|''> }
Loaded by sections/ardor-aframe-builder-ws-ai-tpl.liquid after the builder. */
(function (D) {
  'use strict';
  if (!D || window[D.ns]) return;
  var LAYER = 'Ardor guides - hide before saving';
  var VERSION = 'ardor-template-1';
  var C = { ink: '#1B1C1E', red: '#BD252C', muted: '#55585F', faint: '#A3A6AD', band: '#7A7E86', line: '#C9CBD0', blue: '#3B6FD4' };

  // ---------- small helpers (same text rules as assets/ardor-proofsheet.js) ----------
  function latin1Bytes(s) { var b = new Uint8Array(s.length); for (var i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 255; return b; }
  function concat(chunks) { var n = 0; chunks.forEach(function (c) { n += c.length; }); var o = new Uint8Array(n), p = 0; chunks.forEach(function (c) { o.set(c, p); p += c.length; }); return o; }
  function num(n) { if (!isFinite(n)) n = 0; var r = Math.round(n * 1000) / 1000; return (Math.abs(r) < 1e-9 ? 0 : r).toString(); }
  function rgb(hex) { var n = parseInt(String(hex || '#000000').slice(1), 16); return num(((n >> 16) & 255) / 255) + ' ' + num(((n >> 8) & 255) / 255) + ' ' + num((n & 255) / 255); }
  function fmt(n) { var r = Math.round(n * 100) / 100; return Math.abs(r - Math.round(r)) < 0.001 ? String(Math.round(r)) : String(r); }
  var WIN = { 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97, 0x2018: 0x91, 0x2019: 0x92, 0x201C: 0x93, 0x201D: 0x94, 0x2026: 0x85, 0x20AC: 0x80, 0x2122: 0x99 };
  function pdfText(s) {
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i), b;
      if (c < 128) b = c; else if (c >= 0xA0 && c <= 0xFF) b = c; else if (WIN[c]) b = WIN[c]; else b = 63;
      var ch = String.fromCharCode(b);
      if (ch === '(' || ch === ')' || ch === '\\') ch = '\\' + ch;
      out += ch;
    }
    return '(' + out + ')';
  }
  // Helvetica and Helvetica-Bold widths (per 1000 em)
  var HW = [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584];
  var HBW = [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584];
  function textWidth(s, size, bold) { var t = bold ? HBW : HW, w = 0; s = String(s); for (var i = 0; i < s.length; i++) { var c = s.charCodeAt(i); w += (c >= 32 && c < 127) ? t[c - 32] : 556; } return w * size / 1000; }
  function wrap(s, size, bold, maxW) {
    var lines = [], words = String(s == null ? '' : s).split(/\s+/).filter(Boolean), cur = '';
    words.forEach(function (w) {
      var t = cur ? cur + ' ' + w : w;
      if (textWidth(t, size, bold) <= maxW || !cur) cur = t; else { lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    return lines.length ? lines : [''];
  }
  function deflate(bytes) {
    if (typeof CompressionStream === 'undefined') return Promise.resolve(null);
    try {
      var cs = new CompressionStream('deflate'), w = cs.writable.getWriter();
      w.write(bytes); w.close();
      return new Response(cs.readable).arrayBuffer().then(function (ab) { return new Uint8Array(ab); }, function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  // ---------- PDF writer ----------
  function Doc() { this.objs = [null]; }
  Doc.prototype.alloc = function () { this.objs.push(null); return this.objs.length - 1; };
  Doc.prototype.set = function (id, dict, stream) { this.objs[id] = { dict: dict, stream: stream || null }; return id; };
  Doc.prototype.add = function (dict, stream) { return this.set(this.alloc(), dict, stream); };
  Doc.prototype.bytes = function (rootId, infoId) {
    var chunks = [], off = 0, offsets = [0];
    var push = function (b) { chunks.push(b); off += b.length; };
    push(latin1Bytes('%PDF-1.6\n%\xE2\xE3\xCF\xD3\n'));
    for (var i = 1; i < this.objs.length; i++) {
      var o = this.objs[i] || { dict: 'null' };
      offsets[i] = off;
      if (o.stream) {
        var d = o.dict.replace(/\s*>>\s*$/, '') + ' /Length ' + o.stream.length + ' >>';
        push(latin1Bytes(i + ' 0 obj\n' + d + '\nstream\n')); push(o.stream); push(latin1Bytes('\nendstream\nendobj\n'));
      } else push(latin1Bytes(i + ' 0 obj\n' + o.dict + '\nendobj\n'));
    }
    var xref = off, n = this.objs.length, x = 'xref\n0 ' + n + '\n0000000000 65535 f \n';
    for (var j = 1; j < n; j++) x += String(offsets[j]).padStart(10, '0') + ' 00000 n \n';
    x += 'trailer\n<< /Size ' + n + ' /Root ' + rootId + ' 0 R' + (infoId ? ' /Info ' + infoId + ' 0 R' : '') + ' >>\nstartxref\n' + xref + '\n%%EOF\n';
    push(latin1Bytes(x));
    return concat(chunks);
  };

  // ---------- the Ardor logo (the builder's own logo file), read once ----------
  var logoP = null;
  function loadLogo() {
    if (logoP) return logoP;
    var url = '';
    try { url = D.logoUrl(api()); } catch (e) { url = ''; }
    if (!url) return Promise.resolve(null);
    logoP = fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('logo'); return r.blob(); }).then(function (bl) {
      return new Promise(function (res) { var u = URL.createObjectURL(bl), im = new Image(); im.onload = function () { res(im); }; im.onerror = function () { URL.revokeObjectURL(u); res(null); }; im.src = u; });
    }).then(function (im) {
      var sw = im && (im.naturalWidth || im.width), sh = im && (im.naturalHeight || im.height);
      if (!sw || !sh) throw new Error('logo');
      var k = Math.min(1, 640 / Math.max(sw, sh)), w = Math.max(1, Math.round(sw * k)), h = Math.max(1, Math.round(sh * k));
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var ctx = cv.getContext('2d'); ctx.imageSmoothingQuality = 'high'; ctx.drawImage(im, 0, 0, w, h);
      var d = ctx.getImageData(0, 0, w, h).data, col = new Uint8Array(w * h * 3), al = new Uint8Array(w * h), hasA = false;
      for (var i = 0, p = 0; i < w * h; i++, p += 4) {
        var a = d[p + 3]; al[i] = a; if (a !== 255) hasA = true;
        if (a === 0) { col[i * 3] = 255; col[i * 3 + 1] = 255; col[i * 3 + 2] = 255; }
        else { col[i * 3] = d[p]; col[i * 3 + 1] = d[p + 1]; col[i * 3 + 2] = d[p + 2]; }
      }
      cv.width = cv.height = 1;
      return { w: w, h: h, col: col, al: hasA ? al : null };
    }).catch(function () { logoP = null; return null; });
    return logoP;
  }
  function addLogo(doc, L) {
    if (!L) return Promise.resolve(null);
    return Promise.all([deflate(L.col), L.al ? deflate(L.al) : Promise.resolve(null)]).then(function (z) {
      var smask = '';
      if (L.al) {
        var sid = doc.add('<< /Type /XObject /Subtype /Image /Width ' + L.w + ' /Height ' + L.h + ' /ColorSpace /DeviceGray /BitsPerComponent 8' + (z[1] ? ' /Filter /FlateDecode' : '') + ' >>', z[1] || L.al);
        smask = ' /SMask ' + sid + ' 0 R';
      }
      var id = doc.add('<< /Type /XObject /Subtype /Image /Width ' + L.w + ' /Height ' + L.h + ' /ColorSpace /DeviceRGB /BitsPerComponent 8' + (z[0] ? ' /Filter /FlateDecode' : '') + smask + ' >>', z[0] || L.col);
      return { id: id, w: L.w, h: L.h };
    });
  }

  // ---------- one page: the template for one printed side ----------
  // Sizes in inches, top-left origin; the page is the print size (trim + bleed on every side; the builders print with no bleed).
  function pageOps(sp, pg, logo) {
    var b = sp.bleed || 0, W = sp.W, H = sp.H, s = sp.safe;
    var Wp = W + 2 * b, Hp = H + 2 * b, u = Math.min(W, H), o = [];
    var X = function (x) { return num(x * 72); }, Y = function (y) { return num((Hp - y) * 72); };
    var rect = function (x, y, w, h) { return X(x) + ' ' + Y(y + h) + ' ' + num(w * 72) + ' ' + num(h * 72) + ' re'; };
    var lw = Math.max(0.75, Math.min(2.5, u * 0.07)); // points
    var fsBand = Math.max(7, Math.min(s * 72 * 0.45, u * 0.75)); // points: fits between the cut edge and the safe line
    // x, y: inches (y = baseline from the top). align l / c / r. rot 90 = reads from bottom to top.
    function text(str, x, y, size, bold, color, align, rot) {
      var w = textWidth(str, size, bold) / 72, dx = align === 'c' ? -w / 2 : (align === 'r' ? -w : 0);
      var tm = rot === 90 ? '0 1 -1 0 ' + X(x) + ' ' + num((Hp - y + dx) * 72) : '1 0 0 1 ' + X(x + dx) + ' ' + Y(y);
      o.push('BT /' + (bold ? 'F2' : 'F1') + ' ' + num(size) + ' Tf ' + rgb(color) + ' rg ' + tm + ' Tm ' + pdfText(str) + ' Tj ET');
    }
    var tx = b, ty = b, bandMid = function (fs) { return s / 2 + fs * 0.36 / 72; };
    o.push('/OC /OC1 BDC');
    o.push('q');
    // zone to keep words and logos out of (e.g. the A-frame's frame can cover the band along the edge)
    if (sp.band) o.push('q /GS1 gs ' + rgb(C.band) + ' rg ' + rect(tx, ty, W, H) + ' ' + rect(tx + s, ty + s, W - 2 * s, H - 2 * s) + ' f* Q');
    // bleed (only for a print file with bleed): the page edge is the bleed edge, the cut line is inside it
    if (b > 0) {
      o.push('[] 0 d ' + rgb(C.blue) + ' RG ' + num(lw * 0.6) + ' w ' + rect(lw / 288, lw / 288, Wp - lw / 144, Hp - lw / 144) + ' S');
      o.push(rgb(C.ink) + ' RG ' + num(lw) + ' w ' + rect(tx, ty, W, H) + ' S');
    } else {
      // no bleed: the page edge is the cut edge (drawn just inside the page so all of the line shows)
      o.push('[] 0 d ' + rgb(C.ink) + ' RG ' + num(lw) + ' w ' + rect(lw / 144, lw / 144, Wp - lw / 72, Hp - lw / 72) + ' S');
    }
    // dashed safe line
    o.push('[' + num(lw * 7) + ' ' + num(lw * 5) + '] 0 d ' + rgb(C.red) + ' RG ' + num(lw) + ' w ' + rect(tx + s, ty + s, W - 2 * s, H - 2 * s) + ' S [] 0 d');
    o.push('Q');
    // labels in the band along the edge
    text(b > 0 ? 'Cut line' : 'Cut edge = page edge', tx + Math.min(0.25, s * 0.5), ty + bandMid(fsBand), fsBand, false, C.ink, 'l');
    text(fmt(W) + ' in wide', tx + W / 2, ty + bandMid(fsBand), fsBand, true, C.ink, 'c');
    text(fmt(H) + ' in tall', tx + s / 2 + fsBand * 0.36 / 72, ty + H / 2, fsBand, true, C.ink, 'c', 90);
    if (sp.bottom) text(sp.bottom, tx + W / 2, ty + H - s + bandMid(fsBand), fsBand, false, C.ink, 'c');
    var fsSafe = fsBand;
    text('Safe line: keep words and logos inside it', tx + s + 0.15, ty + s + 0.12 + fsSafe * 0.8 / 72, fsSafe, false, C.red, 'l');
    // the side, big and light, at the top
    var fsSide = Math.max(16, Math.min(64, u * 2.4)), sideBase = ty + s + 0.3 + fsSafe / 72 + fsSide * 0.75 / 72;
    text(pg.label, tx + W / 2, sideBase, fsSide, true, C.faint, 'c');
    // the note card: logo, what this is, how to use it
    var top = sideBase + 0.3, bot = ty + H - s - 0.3, avail = bot - top;
    var bw = Math.min(W - 2 * s - 0.6, Math.max(5, Math.min(13, W * 0.56)));
    var notes = sp.notes(pg);
    var lay = function (fs) {
      var pad = fs * 1.1 / 72, iw = bw - 2 * pad, ind = fs * 1.0 / 72, rows = [], y = pad;
      var lh = fs * 1.9 / 72, Lw = 0;
      if (logo) { Lw = lh * logo.w / logo.h; if (Lw > iw * 0.5) { Lw = iw * 0.5; lh = Lw * logo.h / logo.w; } }
      rows.push({ k: 'head', y: y, h: Math.max(lh, fs * 1.3 / 72), lw: Lw, lh: lh });
      y += Math.max(lh, fs * 1.3 / 72) + fs * 0.75 / 72;
      wrap('Print template: ' + sp.product, fs * 1.2, true, iw * 72).forEach(function (t) { y += fs * 1.2 / 72; rows.push({ k: 't', y: y, t: t, fs: fs * 1.2, bold: true, color: C.ink }); y += fs * 0.3 / 72; });
      wrap(fmt(W) + ' x ' + fmt(H) + ' in (width x height) • ' + pg.name, fs, true, iw * 72).forEach(function (t) { y += fs / 72; rows.push({ k: 't', y: y, t: t, fs: fs, bold: true, color: C.muted }); y += fs * 0.3 / 72; });
      y += fs * 0.45 / 72;
      notes.forEach(function (n) {
        wrap(n, fs, false, (iw - ind) * 72).forEach(function (t, i) { y += fs / 72; rows.push({ k: 'b', y: y, t: t, fs: fs, dot: i === 0 }); y += fs * 0.3 / 72; });
        y += fs * 0.28 / 72;
      });
      y += fs * 0.35 / 72; rows.push({ k: 'rule', y: y }); y += fs * 0.6 / 72;
      wrap('Questions? Call (425) 629-0796 or visit ardorprinting.com.', fs * 0.82, false, iw * 72).forEach(function (t) { y += fs * 0.82 / 72; rows.push({ k: 't', y: y, t: t, fs: fs * 0.82, bold: false, color: C.muted }); y += fs * 0.3 / 72; });
      return { h: y + pad, rows: rows, pad: pad, ind: ind, iw: iw };
    };
    var fs = Math.max(6, bw * 72 / 30), L = lay(fs);
    while (L.h > avail && fs > 6) { fs *= 0.92; L = lay(fs); }
    var bx = tx + (W - bw) / 2, by = top + Math.max(0, (avail - L.h) / 2);
    o.push('q /GS2 gs 1 1 1 rg ' + rect(bx, by, bw, L.h) + ' f Q');
    o.push('q ' + rgb(C.line) + ' RG ' + num(Math.max(0.5, lw * 0.6)) + ' w ' + rect(bx, by, bw, L.h) + ' S Q');
    L.rows.forEach(function (r) {
      if (r.k === 'head') {
        if (logo) o.push('q ' + num(r.lw * 72) + ' 0 0 ' + num(r.lh * 72) + ' ' + X(bx + L.pad) + ' ' + Y(by + r.y + r.lh) + ' cm /Logo Do Q');
        else text('Ardor Printing', bx + L.pad, by + r.y + r.h * 0.7, fs * 1.3, true, C.ink, 'l');
        text('ardorprinting.com', bx + bw - L.pad, by + r.y + r.h / 2 + fs * 0.34 / 72, fs * 0.95, true, C.red, 'r');
      } else if (r.k === 'rule') {
        o.push('q ' + rgb(C.line) + ' RG ' + num(Math.max(0.5, lw * 0.5)) + ' w ' + X(bx + L.pad) + ' ' + Y(by + r.y) + ' m ' + X(bx + bw - L.pad) + ' ' + Y(by + r.y) + ' l S Q');
      } else if (r.k === 'b') {
        if (r.dot) text('•', bx + L.pad, by + r.y, r.fs, true, C.red, 'l');
        text(r.t, bx + L.pad + L.ind, by + r.y, r.fs, false, C.ink, 'l');
      } else text(r.t, bx + L.pad, by + r.y, r.fs, r.bold, r.color, 'l');
    });
    o.push('EMC');
    return { ops: o.join('\n'), Wp: Wp, Hp: Hp };
  }

  // ---------- the whole PDF ----------
  function build(sp) {
    sp = sp || spec();
    return loadLogo().then(function (L) {
      var doc = new Doc(), pagesId = doc.alloc(), rootId = doc.alloc();
      var ocg = doc.add('<< /Type /OCG /Name ' + pdfText(LAYER) + ' >>');
      var f1 = doc.add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
      var f2 = doc.add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
      var g1 = doc.add('<< /Type /ExtGState /ca 0.16 >>');
      var g2 = doc.add('<< /Type /ExtGState /ca 0.92 >>');
      return addLogo(doc, L).then(function (logo) {
        var kids = [];
        sp.pages.forEach(function (pg) {
          var r = pageOps(sp, pg, logo), b = sp.bleed || 0;
          var cid = doc.add('<< >>', latin1Bytes(r.ops));
          var res = '<< /Font << /F1 ' + f1 + ' 0 R /F2 ' + f2 + ' 0 R >> /ExtGState << /GS1 ' + g1 + ' 0 R /GS2 ' + g2 + ' 0 R >>' + (logo ? ' /XObject << /Logo ' + logo.id + ' 0 R >>' : '') + ' /Properties << /OC1 ' + ocg + ' 0 R >> >>';
          var box = '[0 0 ' + num(r.Wp * 72) + ' ' + num(r.Hp * 72) + ']', trim = '[' + num(b * 72) + ' ' + num(b * 72) + ' ' + num((r.Wp - b) * 72) + ' ' + num((r.Hp - b) * 72) + ']';
          kids.push(doc.add('<< /Type /Page /Parent ' + pagesId + ' 0 R /MediaBox ' + box + ' /BleedBox ' + box + ' /TrimBox ' + trim + ' /Resources ' + res + ' /Contents ' + cid + ' 0 R >>'));
        });
        doc.set(pagesId, '<< /Type /Pages /Kids [' + kids.map(function (k) { return k + ' 0 R'; }).join(' ') + '] /Count ' + kids.length + ' >>');
        doc.set(rootId, '<< /Type /Catalog /Pages ' + pagesId + ' 0 R /PageMode /UseOC /OCProperties << /OCGs [' + ocg + ' 0 R] /D << /Name (Ardor template) /Order [' + ocg + ' 0 R] /ON [' + ocg + ' 0 R] /OFF [] >> >> >>');
        var d = new Date(), pad = function (n) { return String(n).padStart(2, '0'); };
        var ds = 'D:' + d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
        var title = 'Ardor Printing print template: ' + sp.product + ', ' + fmt(sp.W) + ' x ' + fmt(sp.H) + ' in, ' + sp.sidesText;
        var inf = doc.add('<< /Title ' + pdfText(title) + ' /Author (Ardor Printing) /Subject (Print template) /Creator (ardorprinting.com) /Producer (Ardor Printing) /Keywords (' + VERSION + ') /CreationDate (' + ds + ') >>');
        var blob = new Blob([doc.bytes(rootId, inf)], { type: 'application/pdf' });
        return { blob: blob, name: fileName(sp), spec: sp };
      });
    });
  }
  function fileName(sp) { return ('Ardor-Template-' + sp.fileProduct + '-' + sp.sizeTag + '-' + sp.sidesTag).replace(/[^A-Za-z0-9.-]+/g, '-').replace(/-+/g, '-') + '.pdf'; }

  // ---------- what the builder has picked ----------
  function api() { return window[D.apiName] || null; }
  function spec() {
    var A = api(); if (!A || !A.cfg) throw new Error('builder not ready');
    var sp = D.spec(A, fmt);
    var s = fmt(sp.safe) + ' in', W = fmt(sp.W), H = fmt(sp.H);
    sp.notes = function (pg) {
      var out = [];
      out.push((sp.bleed > 0 ? 'The page is the print size with bleed. Your sign is cut on the solid line, ' : 'This page is the exact print size, ') + W + ' x ' + H + ' in.' + (sp.bleed > 0 ? '' : ' The page edge is the cut edge, so no bleed is needed.'));
      out.push('Extend the background to the outer edge.');
      out.push('Keep words and logos inside the dashed line (' + s + ' from the edge).');
      (sp.extra || []).forEach(function (t) { out.push(t); });
      if (pg.sameNote) out.push(sp.sameNote);
      out.push('Before you save, hide the layer "' + LAYER + '" or delete these guides.');
      out.push('Save as a PDF, or as a PNG or JPG in this shape, and upload it in our designer. We place it edge to edge.');
      return out;
    };
    return sp;
  }

  // ---------- download ----------
  function saveBlob(blob, name) {
    var url = URL.createObjectURL(blob), a = document.createElement('a');
    a.href = url; a.download = name; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(function () { try { document.body.removeChild(a); URL.revokeObjectURL(url); } catch (e) {} }, 60000);
  }
  var busy = false;
  function download(source, btn) {
    if (busy) return Promise.resolve(null);
    var A = api(); if (!A) return Promise.resolve(null);
    busy = true;
    var btns = Array.prototype.slice.call(document.querySelectorAll('[data-abb-template]'));
    btns.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });
    var done = function () { busy = false; btns.forEach(function (b) { b.removeAttribute('aria-busy'); }); };
    var sp;
    try { sp = spec(); } catch (e) { done(); return Promise.resolve(null); }
    return build(sp).then(function (r) {
      saveBlob(r.blob, r.name);
      try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'ardor_' + D.key + '_template_download', source: source || 'rail', file: r.name }, sp.track || {})); } catch (e) {}
      if (A.toast) A.toast('Template downloaded: ' + r.name);
      done(); return r;
    }, function () {
      if (A.toast) A.toast('We couldn\'t make the template. Please try again, or call us at (425) 629-0796.');
      done(); return null;
    });
  }

  // ---------- does an uploaded PDF still have the guides layer turned on? ----------
  // 'on' = our layer is in the file and not switched off in its default view, 'off' = switched off, '' = not found
  function guides(file) {
    if (!file || !file.size || !/pdf|\.ai$/i.test((file.type || '') + ' ' + (file.name || ''))) return Promise.resolve('');
    return file.arrayBuffer().then(function (ab) {
      var s = new TextDecoder('windows-1252').decode(new Uint8Array(ab));
      var at = s.indexOf('/Name (' + LAYER + ')');
      if (at < 0) return s.indexOf(LAYER) >= 0 ? 'on' : '';
      var head = s.slice(Math.max(0, at - 200), at), re = /(\d+)\s+0\s+obj\b/g, mm, id = '';
      while ((mm = re.exec(head))) id = mm[1];
      if (!id) return 'on';
      var offs = s.match(/\/OFF\s*\[[^\]]*\]/g) || [];
      var last = offs.length ? offs[offs.length - 1] : '';
      return new RegExp('(^|[^0-9])' + id + '\\s+0\\s+R').test(last.replace(/^\/OFF\s*\[/, ' ')) ? 'off' : 'on';
    }).catch(function () { return ''; });
  }

  // ---------- the rail button and the Artwork panel line ----------
  var ICON = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M12 11.5v6m0 0 2.5-2.5M12 17.5 9.5 15"/></svg>';
  var CSS = '' +
    '@media (max-width:749px){.abx-rail [data-atpl-rail]{display:none!important}}' +
    '.abx-rail [data-atpl-rail][aria-busy="true"]{opacity:.6;cursor:progress}' +
    '.abb-tpl-line{margin:10px 0 0}' +
    '.abb-tpl-link{appearance:none;-webkit-appearance:none;display:inline;min-height:0;height:auto;width:auto;margin:0;padding:0;border:0;border-radius:0;background:none;box-shadow:none;font:inherit;font-weight:600;line-height:inherit;letter-spacing:inherit;text-transform:none;color:inherit;text-decoration:underline;text-underline-offset:2px;cursor:pointer}' +
    '.abb-tpl-link:hover,.abb-tpl-link:focus-visible{color:#BD252C}' +
    '.abb-tpl-link[aria-busy="true"]{opacity:.6;cursor:progress}';
  function addCss() {
    if (document.querySelector('style[data-atpl]')) return;
    var st = document.createElement('style'); st.setAttribute('data-atpl', ''); st.textContent = CSS; document.head.appendChild(st);
  }
  function mount() {
    var root = document.querySelector('[data-abb-root]'), rail = root && root.querySelector('.abx-rail');
    if (!rail) return false;
    if (rail.querySelector('[data-atpl-rail]')) return true;
    var btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'abx-tool'; btn.setAttribute('data-atpl-rail', ''); btn.setAttribute('data-abb-template', 'rail');
    btn.title = 'Download the print template for this size'; btn.setAttribute('aria-label', 'Download the print template (PDF) for this size');
    btn.innerHTML = ICON + '<span>Template</span>';
    var after = rail.querySelector('[data-abb-pdf]') || rail.querySelector('[data-abx-quote]');
    if (after && after.parentNode === rail && after.nextSibling) rail.insertBefore(btn, after.nextSibling); else rail.appendChild(btn);
    return true;
  }
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-abb-template]') : null;
    if (!t) return;
    e.preventDefault(); e.stopPropagation();
    download(t.getAttribute('data-abb-template') === 'rail' ? 'rail' : 'panel', t);
  });
  addCss();
  var tries = 0;
  (function again() { if (!mount() && ++tries < 120) setTimeout(again, 150); })();

  window[D.ns] = { spec: spec, build: build, download: download, guides: guides, layer: LAYER, version: VERSION };
})({
  key: 'aframe', ns: 'ArdorAFrameTemplate', apiName: 'ArdorAFrameAPI',
  logoUrl: function (A) { return (A && A.conf && A.conf.logoUrl) || ''; },
  // A-frame: one sign (insert or applied print) per side of the frame; the print file is the exact sign size with no bleed
  // (ardor-aframe-builder-v2-tpl.js). The band between the edge and the dashed line is the part the frame can cover.
  spec: function (A, fmt) {
    var c = A.cfg(), m = c.model, two = c.sides === 2;
    return {
      product: m.name + (m.insert ? ' insert' : ' sign'), fileProduct: 'A-Frame-' + m.name, W: c.Wi, H: c.Hi, bleed: 0, safe: A.safe || 0.75,
      sizeTag: fmt(c.Wi) + 'x' + fmt(c.Hi), sidesTag: two ? '2-sides' : '1-side', sidesText: two ? '2 signs (front and back)' : '1 sign',
      pages: two ? [{ label: 'FRONT SIGN', name: 'Front sign' }, { label: 'BACK SIGN', name: 'Back sign', sameNote: true }] : [{ label: 'FRONT SIGN', name: 'Front sign' }],
      band: true,
      bottom: m.insert ? 'The frame can cover this band' : 'Keep words and logos out of this band',
      extra: [m.insert ? 'The frame can cover the shaded band along the edges. Keep words and logos out of it.' : 'Keep words and logos out of the shaded band along the edges.'],
      sameNote: 'Same art on both signs? You only need the front page.',
      track: { frame: m.id, order: c.order, signs: c.sides }
    };
  }
});
